import { useState } from 'react';
import { useI18n } from '../i18n';
import { api } from '../api/client';
import { validatePasswordStrength } from '../lib/password-policy';

/**
 * 修改密码卡片（设置页）
 *
 * - 验证当前密码 + 两次输入新密码
 * - 成功后清空表单并提示（现有登录态保持有效）
 */
export default function ChangePassword() {
  const { t } = useI18n();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null);
    setSuccess(false);

    const pwdError = validatePasswordStrength(newPassword);
    if (pwdError) {
      setError(t(pwdError));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('settings.password.errMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">{t('settings.password.title')}</span>
      </div>
      <p style={{ fontSize: '0.85rem', color: 'var(--ink2)', marginBottom: '1rem', lineHeight: 1.6 }}>
        {t('settings.password.intro')}
      </p>

      {success && (
        <div className="state-box" style={{ borderColor: 'var(--ok, #16a34a)', marginBottom: '1rem' }}>
          {t('settings.password.success')}
        </div>
      )}
      {error && (
        <div className="field-error" style={{ marginBottom: '0.75rem' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label className="form-label" htmlFor="cp-current">
            {t('settings.password.current')}
          </label>
          <input
            id="cp-current"
            className="form-input"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            disabled={submitting}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="cp-new">
            {t('settings.password.new')}
          </label>
          <input
            id="cp-new"
            className="form-input"
            type="password"
            autoComplete="new-password"
            placeholder={t('settings.password.newPlaceholder')}
            value={newPassword}
            disabled={submitting}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <div className="form-hint">{t('auth.pwdHint')}</div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="cp-confirm">
            {t('settings.password.confirm')}
          </label>
          <input
            id="cp-confirm"
            className="form-input"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            disabled={submitting}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>
        <button className="btn btn-accent" type="submit" disabled={submitting || !currentPassword || !newPassword}>
          {submitting ? t('settings.password.submitting') : t('settings.password.submit')}
        </button>
      </form>
    </div>
  );
}
